import { withAuthRecovery } from '@/lib/authRecovery'
import { getSupabase } from '@/lib/supabase'

/**
 * Операции покупки студии (миграция 20260802160000).
 * Перенос ручных раннбуков supabase/operations в UI:
 *   * planning_and_opening/create_buying_for_existing_partner.sql
 *   * sale_and_purchase/complete_precreated_buying/apply_complete_precreated_buying.sql
 * Все проверки и границы интервалов — на стороне RPC, фронт только собирает форму.
 */

export type StudioPurchaseCandidate = {
  studio_internal_id: number
  studio_number: number | null
  city_name: string | null
  address: string | null
  status_code: string | null
  owner_partner_number: number | null
  owner_persons_fio: string
  owner_start_date: string | null
  owner_phase: string | null
  pending_buying_count: number
  has_active_offer: boolean
}

export type StudioPurchasePartner = {
  partner_number: number
  persons_fio: string
  current_status: string | null
  active_studio_count: number
  cities_text: string
}

export type StudioPurchaseOpenOwnership = {
  ownership_id: number
  series_id: number
  partner_number: number
  persons_fio: string
  ownership_phase: string
  start_date: string | null
  /** 0 = продавец кандидат в «бывшие» после передачи. */
  owner_other_active_studios: number
}

export type StudioPurchasePendingBuying = {
  ownership_id: number
  series_id: number
  partner_number: number
  persons_fio: string
  created_at: string
  series_has_contracts: boolean
}

export type StudioPurchaseContext = {
  studio: {
    studio_internal_id: number
    studio_number: number | null
    status_code: string | null
    city_id: number | null
    city_name: string | null
  }
  business_date: string
  open_ownerships: StudioPurchaseOpenOwnership[]
  pending_buyings: StudioPurchasePendingBuying[]
  active_offer_count: number
  buyer: {
    partner_number: number
    exists: boolean
    persons_fio: string
    has_shmp_group_in_city: boolean
    owns_this_studio: boolean
  } | null
}

export type PlanStudioPurchaseResult = {
  studio_internal_id: number
  studio_number: number | null
  buyer_partner_number: number
  seller_partner_number: number | null
  series_id: number
  ownership_id: number
}

export type CancelPlannedStudioPurchaseResult = {
  ownership_id: number
  series_id: number
  series_dropped: boolean
  studio_internal_id: number
}

export type CompleteStudioPurchaseResult = {
  studio_internal_id: number
  studio_number: number | null
  transfer_date: string
  seller_partner_number: number
  seller_ownership_id: number
  seller_end_date: string
  buyer_partner_number: number
  buyer_ownership_id: number
  buyer_series_id: number
  used_precreated_buying: boolean
  closed_offers: number
  shmp_group_created: boolean
  seller_marked_former: boolean
  seller_other_active_studios: number
}

/** Живая серия без MAIN-договора — единица выбора в кнопке «Новый ДКК» реестра. */
export type ContractPendingSeries = {
  series_id: number
  partner_number: number
  persons_fio: string
  description: string | null
  series_created_at: string
  ownership_id: number
  ownership_phase: string
  ownership_start_date: string | null
  studio_internal_id: number
  studio_number: number | null
  city_name: string | null
  studio_status: string | null
}

export async function fetchStudioPurchaseCandidates(): Promise<StudioPurchaseCandidate[]> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('list_studio_purchase_candidates')
  )) as { data: StudioPurchaseCandidate[] | null; error: any }

  if (error) throw error
  return data ?? []
}

export async function fetchStudioPurchasePartners(): Promise<StudioPurchasePartner[]> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('list_studio_purchase_partners')
  )) as { data: StudioPurchasePartner[] | null; error: any }

  if (error) throw error
  return data ?? []
}

export async function fetchStudioPurchaseContext(
  studioInternalId: number,
  buyerPartnerNumber?: number | null
): Promise<StudioPurchaseContext> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('get_studio_purchase_context', {
      p_studio_internal_id: studioInternalId,
      p_buyer_partner_number: buyerPartnerNumber ?? null,
    })
  )) as { data: StudioPurchaseContext | null; error: any }

  if (error) throw error
  if (!data) throw new Error('Контекст студии не получен')
  return data
}

export async function planStudioPurchase(params: {
  studioInternalId: number
  buyerPartnerNumber: number
  note?: string | null
}): Promise<PlanStudioPurchaseResult> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('plan_studio_purchase', {
      p_studio_internal_id: params.studioInternalId,
      p_buyer_partner_number: params.buyerPartnerNumber,
      p_note: params.note?.trim() || null,
    })
  )) as { data: PlanStudioPurchaseResult | null; error: any }

  if (error) throw error
  if (!data) throw new Error('Покупка не запланирована')
  return data
}

export async function cancelPlannedStudioPurchase(params: {
  ownershipId: number
  note?: string | null
}): Promise<CancelPlannedStudioPurchaseResult> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('cancel_planned_studio_purchase', {
      p_ownership_id: params.ownershipId,
      p_note: params.note?.trim() || null,
    })
  )) as { data: CancelPlannedStudioPurchaseResult | null; error: any }

  if (error) throw error
  if (!data) throw new Error('Планируемая покупка не отменена')
  return data
}

/** Серии без ДКК (запланированная покупка, новая студия, живая студия без договора). */
export async function fetchContractPendingSeries(): Promise<ContractPendingSeries[]> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('list_contract_pending_series')
  )) as { data: ContractPendingSeries[] | null; error: any }

  if (error) throw error
  return data ?? []
}

/**
 * ДКК внутри уже существующего финансового контура (серии).
 * Реестр документов заводит договор только так: партнёра и студию создают на
 * экране «Исходные данные». Путь «Продление ДКК» тут не годится — он создал бы
 * договор в серии источника.
 */
export async function createContractForSeries(params: {
  seriesId: number
  contractNumber: string
  signedAt?: string | null
  effectiveFrom?: string | null
  expectedEndDate?: string | null
  entityId?: number | null
}): Promise<number> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('create_contract_for_series', {
      p_series_id: params.seriesId,
      p_contract_number: params.contractNumber.trim(),
      p_signed_at: params.signedAt || null,
      p_effective_from: params.effectiveFrom || null,
      p_expected_end_date: params.expectedEndDate || null,
      p_entity_id: params.entityId ?? null,
    })
  )) as { data: number | null; error: any }

  if (error) throw error
  if (data == null) throw new Error('ДКК не создан')
  return data
}

export async function completeStudioPurchase(params: {
  studioInternalId: number
  buyerPartnerNumber: number
  transferDate: string
  buyingOwnershipId?: number | null
  sellerExitsNetwork?: boolean
  note?: string | null
}): Promise<CompleteStudioPurchaseResult> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('complete_studio_purchase', {
      p_studio_internal_id: params.studioInternalId,
      p_buyer_partner_number: params.buyerPartnerNumber,
      p_transfer_date: params.transferDate,
      p_buying_ownership_id: params.buyingOwnershipId ?? null,
      p_seller_exits_network: params.sellerExitsNetwork ?? false,
      p_note: params.note?.trim() || null,
    })
  )) as { data: CompleteStudioPurchaseResult | null; error: any }

  if (error) throw error
  if (!data) throw new Error('Покупка не проведена')
  return data
}
